import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Field, Input, Select, useToast } from '@/components/ui';
import { useMe } from '@/features/auth/session-context';
import {
  locumContractErrorKind,
  useSaveLocumContract,
  type LocumContract,
} from '@/features/locum/api';
import { isApiError } from '@/lib/api';
import parity from '@/pages/ops/parity.module.css';

/**
 * Add or edit one locum contract (Staff & roles, and RL-1800 the Locums
 * page). RL-1802 (decision D26): contracts are hourly only; an older
 * per-session contract becomes hourly when saved.
 */
export function LocumContractEditor({
  locumUserId,
  roleBranch,
  allowedBranchIds,
  contract,
  onCancel,
  onSaved,
}: {
  locumUserId: string;
  /** The branch code or name of the person's locum role, if limited to one. */
  roleBranch?: string | null;
  /**
   * RL-1800 review: the branches the person is a locum at (null or absent =
   * every branch of the caller); the branch list offers only these.
   */
  allowedBranchIds?: string[] | null;
  contract: LocumContract | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const me = useMe();
  const toast = useToast();
  const save = useSaveLocumContract();
  const branches = allowedBranchIds
    ? me.branches.filter((b) => allowedBranchIds.includes(b.id))
    : me.branches;
  const initialBranch =
    branches.find(
      (branch) => branch.code === roleBranch || branch.name === roleBranch,
    )?.id ??
    branches[0]?.id ??
    '';
  const [form, setForm] = useState({
    branchId: contract?.branchId ?? initialBranch,
    startsOn: contract?.startsOn ?? '',
    endsOn: contract?.endsOn ?? '',
    rate: contract ? String(contract.rateSen / 100) : '',
    notes: contract?.notes ?? '',
  });
  const rate = Number(form.rate);
  const invalid =
    !form.branchId ||
    !form.startsOn ||
    !form.endsOn ||
    form.endsOn < form.startsOn ||
    !Number.isFinite(rate) ||
    rate <= 0;
  const apiError = isApiError(save.error) ? save.error : undefined;
  const errorKind = locumContractErrorKind(save.error);

  return (
    <form
      className={parity.form}
      onSubmit={(event) => {
        event.preventDefault();
        if (invalid) return;
        save.mutate(
          {
            ...(contract ? { id: contract.id } : {}),
            locumUserId,
            branchId: form.branchId,
            startsOn: form.startsOn,
            endsOn: form.endsOn,
            rateBasis: 'hourly',
            rateSen: Math.round(rate * 100),
            ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
          },
          {
            onSuccess: () => {
              toast.show({ tone: 'success', title: t('staff.contractSaved') });
              onSaved();
            },
          },
        );
      }}
    >
      <Field label={t('locum.branch')}>
        <Select
          value={form.branchId}
          placeholder={t('locum.chooseBranch')}
          options={branches.map((branch) => ({
            value: branch.id,
            label: branch.name,
          }))}
          onChange={(event) =>
            setForm({ ...form, branchId: event.target.value })
          }
        />
      </Field>
      <div className={parity.grid2}>
        <Field label={t('locum.startsOn')}>
          <Input
            type="date"
            value={form.startsOn}
            onChange={(event) =>
              setForm({ ...form, startsOn: event.target.value })
            }
          />
        </Field>
        <Field label={t('locum.endsOn')}>
          <Input
            type="date"
            value={form.endsOn}
            onChange={(event) =>
              setForm({ ...form, endsOn: event.target.value })
            }
          />
        </Field>
      </div>
      {contract?.rateBasis === 'per_session' && (
        <p className={parity.notice}>{t('locum.perSessionNowHourly')}</p>
      )}
      <Field label={t('locum.hourlyRate')}>
        <Input
          type="number"
          min="0.01"
          step="0.01"
          value={form.rate}
          onChange={(event) => setForm({ ...form, rate: event.target.value })}
        />
      </Field>
      <Field label={t('locum.notes')}>
        <textarea
          className={parity.textarea}
          value={form.notes}
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
        />
      </Field>
      {apiError && (
        <p className={parity.notice} role="alert">
          {errorKind === 'not_found'
            ? t('locum.errors.notFoundManage')
            : errorKind === 'invalid_rate'
              ? t('locum.errors.invalidRate')
              : apiError.message}
          {apiError.requestId && (
            <span className={parity.sub}>
              {t('common.requestId', { id: apiError.requestId })}
            </span>
          )}
        </p>
      )}
      <div className={parity.rowActions}>
        <Button type="button" onClick={onCancel} disabled={save.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={save.isPending}
          disabled={invalid}
        >
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}
